import {
  buildingOptions,
  getFoodUserBalancesAsOf,
  snackSupplierOptions,
  buildingUsersOptions,
  companiesNamesOptions,
} from "@/api";
import DisabledWalletsTable from "@/components/food/DisabledWalletsTable";
import PageBackButton from "@/components/PageBackButton";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import Table from "@/components/table/Table";
import { useSessionState } from "@/hooks/useSessionState";
import { useTableSearch } from "@/hooks/useTableSearch";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import {
  dateInputStringToDate,
  dateToDateInputString,
  downloadArrayAsCSV,
  downloadArrayAsXlsx,
  EXCEL_NOK_FORMAT,
} from "@/lib";
import { DateString, User } from "@/types";
import {
  Breadcrumbs,
  Button,
  DateInput,
  Dropdown,
  Icon,
  InputField,
  LoadingSpinner,
  Option,
  PopupCard,
} from "@app-components";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { useRouterStore } from "@/store/zustandStore";
import { useBuildingQuery } from "@/components/RouterSetup";
import { endOfMonth, isToday, subMonths } from "date-fns";

const columnHelper = createColumnHelper<Partial<User>>();

export const Route = createFileRoute("/_portal/lunch-admin/overview/users/")({
  component: RouteComponent,
  loader: () => {
    const { queryClient } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(buildingUsersOptions);
    queryClient.ensureQueryData(
      companiesNamesOptions(null, { getAllIfNoCompanyIds: true }),
    );
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  useWelcomeFoodAccess();
  const navigate = useNavigate();
  const router = useRouter();
  const { data: users, isLoading } = useBuildingQuery(buildingUsersOptions);
  const { data: companyNames, isLoading: isLoadingCompanyNames } =
    useBuildingQuery(
      companiesNamesOptions(null, { getAllIfNoCompanyIds: true }),
    );
  const companyMap = useMemo(
    () =>
      new Map(
        companyNames?.map((c) => [c.buildingTenantId, c.buildingTenantName]) ??
          [],
      ),
    [companyNames],
  );
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportDate, setExportDate] = useState<DateString>(
    dateToDateInputString(endOfMonth(subMonths(new Date(), 1))),
  );
  const [isExporting, setIsExporting] = useState("");
  const { data: building } = useBuildingQuery(buildingOptions);
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building?.supplierCompanyId),
  );

  const [companiesFilter, setCompaniesFilter] = useSessionState<Option[]>(
    "food-admin-users-company-filter",
    [],
  );
  const { filteredData, searchTerm, searchInputProps } = useTableSearch({
    data: users,
    searchableColumns: [
      { accessor: (user) => [user.firstName, user.lastName] },
      { accessor: (user) => user.email },
      { accessor: (user) => user.phone },
      { accessor: (user) => companyMap.get(user.buildingTenantId) },
    ],
    inputProps: {
      placeholder: t("FOOD_ADMIN.USERS.SEARCH_PLACEHOLDER"),
    },
    filters: [
      {
        accessor: (user) => user.buildingTenantId,
        value: companiesFilter?.map((c) => c.value),
      },
    ],
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => [row.firstName, row.lastName], {
        id: "name",
        cell: (info) => {
          const [firstName, lastName] = info.getValue();
          return `${firstName} ${lastName}`;
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("USERS.TABLE_HEADERS.NAME")}
          />
        ),
        meta: {
          label: t("USERS.TABLE_HEADERS.NAME"),
        },
      }),
      columnHelper.accessor("phone", {
        header: t("USERS.TABLE_HEADERS.PHONE"),
        meta: {
          label: t("USERS.TABLE_HEADERS.PHONE"),
        },
      }),
      columnHelper.accessor("email", {
        header: t("USERS.TABLE_HEADERS.EMAIL"),
        meta: {
          label: t("USERS.TABLE_HEADERS.EMAIL"),
        },
      }),
      columnHelper.accessor("buildingTenantId", {
        cell: (info) => {
          const companyId = info.getValue();
          if (isLoadingCompanyNames) {
            return <LoadingSpinner size={12} />;
          }
          return companyMap.get(companyId);
        },
        header: t("USERS.TABLE_HEADERS.COMPANY"),
        meta: {
          label: t("USERS.TABLE_HEADERS.COMPANY"),
        },
      }),
      columnHelper.accessor("integratedWithWelcomeFood", {
        cell: (info) => {
          const isIntegrated = info.getValue();
          return isIntegrated === true ? (
            <>
              <Icon name="burger" className={`user-has-food-icon greenIcon`} />
              <Tooltip
                anchorSelect=".user-has-food-icon"
                content={t("USERS.FOOD.INTEGRATED")}
              />
            </>
          ) : isIntegrated === false ? (
            <>
              <Icon
                name="no_burger"
                className={`user-deactivated-food-icon dangerIcon`}
                style={{ opacity: 0.5 }}
              />
              <Tooltip
                anchorSelect=".user-deactivated-food-icon"
                content={t("USERS.FOOD.DEACTIVATED")}
              />
            </>
          ) : (
            <>
              <Icon name="no_burger" className={`user-no-food-icon`} />
              <Tooltip
                anchorSelect=".user-no-food-icon"
                content={t("USERS.FOOD.NOT_INTEGRATED")}
              />
            </>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={
              <span className="sr-only">{t("USERS.TABLE_HEADERS.FOOD")}</span>
            }
          />
        ),
        meta: {
          label: t("USERS.TABLE_HEADERS.FOOD"),
        },
      }),
    ],
    [companyMap, isLoadingCompanyNames],
  );

  const handleExport = async (excel?: boolean) => {
    if (!exportDate) return;
    setIsExporting(excel ? "excel" : "csv");
    try {
      const result = await getFoodUserBalancesAsOf(exportDate);
      const balanceUsers = result?.users ?? [];

      // Join balances (identified by userId) with building users for names.
      const nameMap = new Map(
        (users ?? []).map((user) => [
          user.userId,
          `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
        ]),
      );

      const exportData = balanceUsers.map((user) => ({
        ...user,
        name: nameMap.get(user.userId) ?? "",
      }));

      const headers = ["name", "email", "companyName", "balance"];
      const fileName = `lunch-user-balances-${exportDate}`;

      if (excel) {
        downloadArrayAsXlsx(exportData, fileName, headers, undefined, {
          balance: EXCEL_NOK_FORMAT,
        });
      } else {
        downloadArrayAsCSV(exportData, fileName, headers);
      }
      setExportModalOpen(false);
    } finally {
      setIsExporting("");
    }
  };

  const breadcrumbs = [
    { label: t("SIDEBAR.LUNCH_OVERVIEW"), path: "/lunch-admin/overview" },
    {
      label: t("SIDEBAR.INSPECT_LUNCH_USERS"),
      path: "/lunch-admin/overview/users",
    },
  ];

  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/lunch-admin/overview",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{t("SIDEBAR.INSPECT_LUNCH_USERS")}</h1>
        <p>{t("FOOD_ADMIN.USERS.SUBHEADER")}</p>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-sm)",
        }}
      >
        <InputField {...searchInputProps} />
        <Dropdown
          isMulti
          compact
          clearable
          hideLabel
          label={t("FOOD_ADMIN.USERS.FILTER_COMPANY_LABEL")}
          placeholder={t("FOOD_ADMIN.USERS.FILTER_COMPANY_PLACEHOLDER")}
          options={companyNames?.map((c) => ({
            label: c.buildingTenantName!,
            value: c.buildingTenantId!,
          }))}
          value={companiesFilter}
          onChange={(val) => setCompaniesFilter(val ?? [])}
          isLoadingOptions={isLoadingCompanyNames}
          noResultsText={t("DEFAULT_SELECT.NO_RESULTS")}
        />
      </div>
      <Table
        name="food-admin-users-table"
        columns={columns}
        data={filteredData}
        isLoading={isLoading}
        defaultSorting={[
          {
            id: "name",
            desc: false,
          },
        ]}
        onRowClick={({ userId = "" }) =>
          navigate({
            to: "/lunch-admin/overview/users/$userId",
            params: { userId },
          })
        }
        onRowHover={({ userId = "" }) =>
          router.preloadRoute({
            to: "/lunch-admin/overview/users/$userId",
            params: { userId },
          })
        }
        withPagination
      />
      {!!building?.foodWalletRefundEnabled && (
        <DisabledWalletsTable
          users={users}
          searchTerm={searchTerm}
          companyIds={companiesFilter?.map((c) => c.value)}
        />
      )}
      <Button
        secondary
        icon="file_export"
        onClick={() => setExportModalOpen(true)}
        style={{ float: "right", marginTop: "var(--wlcm-spacing-md)" }}
      >
        {t("FOOD_ADMIN.USERS.EXPORT")}
      </Button>
      <PopupCard
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      >
        <h3>{t("FOOD_ADMIN.USERS.EXPORT_MODAL_TITLE")}</h3>
        <p>{t("FOOD_ADMIN.USERS.EXPORT_DATE_HELPER")}</p>
        <DateInput
          dark
          label={t("FOOD_ADMIN.USERS.EXPORT_DATE_LABEL")}
          value={exportDate}
          minDate={
            supplier?.earliestTransactionDate
              ? (supplier.earliestTransactionDate as DateString)
              : undefined
          }
          maxDate={dateToDateInputString(new Date())}
          onChange={(e) => setExportDate(e.target.value as DateString)}
          infoText={isToday(dateInputStringToDate(exportDate)) ? t("FOOD_ADMIN.USERS.EXPORT_DATE_TODAY_HELPER") : undefined}
          style={{ marginTop: "var(--wlcm-spacing-md)" }}
        />
        <div
          style={{
            display: "flex",
            gap: "var(--wlcm-spacing-sm)",
            justifyContent: "flex-end",
            marginTop: "var(--wlcm-spacing-sm)",
          }}
        >
          <Button
            icon="csv"
            disabled={!exportDate || !!isExporting}
            isLoading={isExporting === "csv"}
            onClick={() => handleExport(false)}
          >
            CSV
          </Button>
          <Button
            icon="microsoft_excel"
            disabled={!exportDate || !!isExporting}
            isLoading={isExporting === "excel"}
            onClick={() => handleExport(true)}
          >
            Microsoft Excel
          </Button>
        </div>
      </PopupCard>
    </>
  );
}
